import {
  DescItem,
  formatDate,
  ExternalIcon,
  Badge,
  BadgeProps,
} from '@setel/portal-ui';
import tw from 'twin.macro';
import * as React from 'react';
import { environments } from 'environments/environment';
import { CustomDescList } from 'modules/accounts/account-details';
import { IdentityTypeMap } from 'shared/consts/account.const';
import { IUserProfile } from 'services/api-accounts.service';
import { useLocation } from 'react-router-dom';
import { pageview } from 'shared/analytics/gtag';
import { IUserWalletParams } from 'services/api-ops.service';
import {
  IUserVerifications,
  VerificationStatus,
} from 'services/api-verifications.service';
import { ChatTags } from 'shared/enums/tags.enum';

type UserInfoProps = {
  userProfile: IUserProfile | undefined;
  userWallet: IUserWalletParams | undefined;
  userVerifications: IUserVerifications | undefined;
  ticketTags?: string[] | [];
};

export const ColorMap: Record<VerificationStatus, BadgeProps['color']> = {
  [VerificationStatus.APPROVED]: 'success',
  [VerificationStatus.REJECTED]: 'error',
  [VerificationStatus.PENDING]: 'lemon',
};

export function UserProfile({
  userProfile,
  userWallet,
  userVerifications,
  ticketTags,
}: UserInfoProps) {
  let location = useLocation();
  React.useEffect(() => {
    pageview(location.pathname, 'User Profile');
  }, [location]);
  const transactionURI = identifyTransactionTags(ticketTags);
  return (
    <>
      <div css={tw`border-gray-200 border`}>
        {userProfile && (
          <React.Fragment>
            <CustomDescList>
              <DescItem label="Name" value={userProfile?.fullName} />
              <DescItem
                valueClassName="mt-2"
                labelClassName="mt-2"
                label="eKYC status"
                value={
                  <Badge
                    className="tracking-wider font-semibold uppercase pt-1"
                    rounded="rounded"
                    color={
                      (userVerifications?.verificationStatus &&
                        ColorMap[userVerifications?.verificationStatus]) ||
                      'grey'
                    }
                    style={{ width: 'fit-content' }}
                  >
                    {userVerifications?.verificationStatus ?? 'UNAUTHORIZED'}
                  </Badge>
                }
              />
              <DescItem
                valueClassName="mt-2"
                labelClassName="mt-2"
                label="ID type"
                value={
                  userProfile?.identityType
                    ? IdentityTypeMap(userProfile.identityType)
                    : '-'
                }
              />
              <DescItem
                valueClassName="mt-2"
                labelClassName="mt-2"
                label="ID number"
                value={userProfile?.identityNumber ?? '-'}
              />
              <DescItem
                valueClassName="mt-2"
                labelClassName="mt-2"
                label="Phone number"
                value={userProfile?.phone}
              />
              <DescItem
                valueClassName="mt-2"
                labelClassName="mt-2"
                label="Email"
                value={userProfile?.email}
              />
              <DescItem
                valueClassName="mt-2"
                labelClassName="mt-2"
                label="Wallet Balance"
                value={userWallet?.balance ? `RM${userWallet.balance}` : '-'}
              />
              <DescItem
                valueClassName="mt-2"
                labelClassName="mt-2"
                label="Wallet Limit"
                value={userWallet?.limit ? `RM${userWallet.limit}` : '-'}
              />
              <DescItem
                valueClassName="mt-2"
                labelClassName="mt-2"
                label="Created on"
                value={userProfile && formatDate(userProfile.createdAt)}
              />
            </CustomDescList>
            {transactionURI && (
              <>
                <hr className="mx-4" />
                <a
                  href={`${transactionURI}`}
                  className="text-brand-500 tracking-1 font-semibold my-4 text-center flex justify-center"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="text-xs">VIEW TRANSACTIONS DETAILS </span>
                  <ExternalIcon className="ml-2 inline" />
                </a>
              </>
            )}
            <hr className="mx-4" />
            <a
              href={`${environments.webAdminUrl}/customers/${userProfile.userId}?tabIndex=1`}
              className="text-brand-500 tracking-1 font-semibold my-4 text-center flex justify-center"
              target="_blank"
              rel="noreferrer"
            >
              <span className="text-xs">VIEW ACCOUNT DETAILS </span>
              <ExternalIcon className="ml-2 inline" />
            </a>
          </React.Fragment>
        )}
      </div>
    </>
  );
}

const identifyTransactionTags = (tags: string[] = []) => {
  if (tags.length < 1) {
    return undefined;
  }

  const transaction = tags.find(
    (el) =>
      el.includes(ChatTags.mesra) ||
      el.includes(ChatTags.payment) ||
      el.includes(ChatTags.fuelorder) ||
      el.includes(ChatTags.shopincar) ||
      el.includes(ChatTags.storeorder) ||
      el.includes(ChatTags.ewallet) ||
      el.includes(ChatTags.checkout) ||
      el.includes(ChatTags.invenco) ||
      el.includes(ChatTags.parking) ||
      el.includes(ChatTags.insurance) ||
      el.includes(ChatTags.posorder)
  );

  if (transaction) {
    if (transaction.indexOf(ChatTags.mesra) > -1) {
      return `${
        environments.webAdminUrl
      }/loyalty/members/transaction/${transaction.replace(ChatTags.mesra, '')}`;
    }

    if (transaction.indexOf(ChatTags.fuelorder) > -1) {
      return `${
        environments.webAdminUrl
      }/retail/fuel-orders/${transaction.replace(ChatTags.fuelorder, '')}`;
    }

    if (transaction.indexOf(ChatTags.shopincar) > -1) {
      return `${
        environments.webAdminUrl
      }/store-orders/concierge/${transaction.replace(ChatTags.shopincar, '')}`;
    }

    if (transaction.indexOf(ChatTags.storeorder) > -1) {
      return `${
        environments.webAdminUrl
      }/store-orders/over-counter/${transaction.replace(
        ChatTags.storeorder,
        ''
      )}`;
    }

    if (transaction.indexOf(ChatTags.parking) > -1) {
      return `${
        environments.webAdminUrl
      }/parking-affiliate/sessions/${transaction.replace(
        ChatTags.parking,
        ''
      )}`;
    }

    return `${
      environments.webAdminUrl
    }/payments/transactions/${transaction.substring(
      transaction.indexOf('ctionid_') + 8
    )}`;
  }
  return undefined;
};
